import React, { useState } from 'react';
import AdUnit from '../components/AdUnit';
import SEO from '../components/SEO';

const UuidGeneratorPage = () => {
  const [uuids, setUuids] = useState([]);
  const [quantity, setQuantity] = useState(5);
  const [version, setVersion] = useState('v4');
  const [uppercase, setUppercase] = useState(false);
  const [hyphens, setHyphens] = useState(true);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // UUID v4 generator (random)
  const generateUUIDv4 = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // UUID v1-like (timestamp-based, simplified)
  const generateUUIDv1 = () => {
    const now = Date.now();
    const timeHex = now.toString(16).padStart(12, '0');
    const timeLow = timeHex.slice(-8);
    const timeMid = timeHex.slice(-12, -8);
    const timeHi = '1' + timeHex.slice(0, 3);
    const clockSeq = (Math.random() * 0x3fff | 0x8000).toString(16);
    const node = Array.from({length: 6}, () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0')).join('');
    return `${timeLow}-${timeMid}-${timeHi}-${clockSeq}-${node}`;
  };

  // UUID v7 (timestamp + random, modern)
  const generateUUIDv7 = () => {
    const timestamp = Date.now();
    const timestampHex = timestamp.toString(16).padStart(12, '0');
    const randomBits = Array.from({length: 16}, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `${timestampHex.slice(0, 8)}-${timestampHex.slice(8, 12)}-7${randomBits.slice(0, 3)}-${(parseInt(randomBits.slice(3, 4), 16) & 0x3 | 0x8).toString(16)}${randomBits.slice(4, 7)}-${randomBits.slice(7, 19)}`.slice(0, 36);
  };

  // Nil UUID
  const generateNilUUID = () => {
    return '00000000-0000-0000-0000-000000000000';
  };

  // Max UUID
  const generateMaxUUID = () => {
    return 'ffffffff-ffff-ffff-ffff-ffffffffffff';
  };

  // Generate UUIDs based on version
  const generateUUID = () => {
    switch (version) {
      case 'v1':
        return generateUUIDv1();
      case 'v4':
        return generateUUIDv4();
      case 'v7':
        return generateUUIDv7();
      case 'nil':
        return generateNilUUID();
      case 'max':
        return generateMaxUUID();
      default:
        return generateUUIDv4();
    }
  };

  // Format UUID based on options
  const formatUUID = (uuid) => {
    let formatted = uuid;
    if (!hyphens) {
      formatted = formatted.replace(/-/g, '');
    }
    if (uppercase) {
      formatted = formatted.toUpperCase();
    }
    return formatted;
  };

  // Generate multiple UUIDs
  const generateUUIDs = () => {
    const newUUIDs = Array.from({ length: quantity }, () => formatUUID(generateUUID()));
    setUuids(newUUIDs);
  };

  // Copy single UUID
  const copyToClipboard = async (uuid, index) => {
    try {
      await navigator.clipboard.writeText(uuid);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Copy all UUIDs
  const copyAllToClipboard = async () => {
    if (uuids.length === 0) return;
    try {
      await navigator.clipboard.writeText(uuids.join('\n'));
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Download as text file
  const downloadAsFile = () => {
    if (uuids.length === 0) return;
    const content = uuids.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uuids-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Clear all
  const clearAll = () => {
    setUuids([]);
  };

  // UUID versions info
  const versionInfo = {
    v1: { name: 'UUID v1', description: 'Time-based. Uses timestamp and node ID. May expose MAC address.' },
    v4: { name: 'UUID v4', description: 'Random. Most commonly used. 122 random bits.' },
    v7: { name: 'UUID v7', description: 'Modern time-ordered. Sortable by creation time. Recommended for databases.' },
    nil: { name: 'Nil UUID', description: 'Special UUID with all zeros. Used as a placeholder.' },
    max: { name: 'Max UUID', description: 'Special UUID with all ones. Useful for range queries.' }
  };

  return (
    <div>
      <SEO 
        title="UUID Generator - Generate UUIDs v1, v4, v7 Online" 
        description="Free online UUID generator. Generate UUID v1, v4, v7, nil and max UUIDs. Bulk generation, customizable format, copy and download options."
        keywords="uuid generator, guid generator, uuid v4, uuid v7, unique id generator, uuid online, bulk uuid, random uuid"
      />
      
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
        <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>UUID Generator</h1>
        <p style={{ marginBottom: '20px', color: '#666' }}>
          Generate universally unique identifiers (UUIDs/GUIDs). Choose from different versions and customize the output format.
        </p>
        
        <AdUnit />

        <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '30px', marginTop: '20px' }}>
          {/* Settings Section */}
          <div className="settings-section">
            <h2 style={{ fontSize: '1.3rem', marginBottom: '15px' }}>Settings</h2>
            
            {/* UUID Version */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                UUID Version:
              </label>
              <select
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="v4">UUID v4 (Random) - Recommended</option>
                <option value="v7">UUID v7 (Time-ordered)</option>
                <option value="v1">UUID v1 (Timestamp)</option>
                <option value="nil">Nil UUID (All zeros)</option>
                <option value="max">Max UUID (All ones)</option>
              </select>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '6px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                {versionInfo[version].description}
              </div>
            </div>

            {/* Quantity */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quantity: {quantity}
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                <span>1</span>
                <span>100</span>
              </div>
            </div>

            {/* Quick Quantity Buttons */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Quick Select:
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {[1, 5, 10, 25, 50, 100].map((num) => (
                  <button
                    key={num}
                    onClick={() => setQuantity(num)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: quantity === num ? '#2196f3' : 'white',
                      color: quantity === num ? 'white' : '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {/* Format Options */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '12px', fontWeight: '500' }}>
                Format Options:
              </label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={hyphens}
                    onChange={(e) => setHyphens(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Include hyphens (xxxxxxxx-xxxx-...)</span>
                </label>
                
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={uppercase}
                    onChange={(e) => setUppercase(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Uppercase (XXXXXXXX-XXXX-...)</span>
                </label>
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={generateUUIDs}
              style={{
                width: '100%',
                padding: '14px 20px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                marginBottom: '10px'
              }}
            >
              🎲 Generate UUIDs
            </button>

            {/* Clear Button */}
            <button
              onClick={clearAll}
              style={{
                width: '100%',
                padding: '10px 20px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              🗑️ Clear All
            </button>
          </div>

          {/* Output Section */}
          <div className="output-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ fontSize: '1.3rem', margin: 0 }}>Generated UUIDs ({uuids.length})</h2>
              {uuids.length > 0 && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={copyAllToClipboard}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: copiedAll ? '#4caf50' : '#e0e0e0',
                      color: copiedAll ? 'white' : '#333',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {copiedAll ? '✓ Copied All' : '📋 Copy All'}
                  </button>
                  <button
                    onClick={downloadAsFile}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    📥 Download
                  </button>
                </div>
              )}
            </div>
            
            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '15px',
                backgroundColor: '#fafafa',
                minHeight: '400px',
                maxHeight: '600px',
                overflowY: 'auto'
              }}
            >
              {uuids.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '60px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}>🎲</div>
                  <div style={{ fontSize: '16px', marginBottom: '8px' }}>No UUIDs generated yet</div>
                  <div style={{ fontSize: '14px' }}>Click "Generate UUIDs" to create new identifiers</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uuids.map((uuid, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        backgroundColor: 'white',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ color: '#999', fontSize: '12px', minWidth: '30px' }}>
                          {index + 1}.
                        </span>
                        <code style={{ fontFamily: 'monospace', fontSize: '14px', color: '#333' }}>
                          {uuid}
                        </code>
                      </div>
                      <button
                        onClick={() => copyToClipboard(uuid, index)}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: copiedIndex === index ? '#4caf50' : 'white',
                          color: copiedIndex === index ? 'white' : '#666',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {copiedIndex === index ? '✓' : '📋'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="info-section" style={{ marginTop: '40px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '15px' }}>About UUIDs</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>What is a UUID?</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                UUID (Universally Unique Identifier) is a 128-bit identifier that is unique across space and time.
                Also known as GUID (Globally Unique Identifier) in Microsoft systems. UUIDs are used to identify
                records, sessions, transactions, and resources without central coordination.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>UUID Versions</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                <strong>v1:</strong> Time-based with node ID<br/>
                <strong>v4:</strong> Random (most common)<br/>
                <strong>v7:</strong> Time-ordered, sortable (modern)<br/>
                For new projects, UUID v4 or v7 is recommended.
              </p>
            </div>
            
            <div>
              <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: '#333' }}>Common Use Cases</h3>
              <p style={{ fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                Database primary keys, distributed systems, session identifiers, file names,
                API keys, tracking IDs, and any scenario requiring unique identification without collision risk.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @media (max-width: 768px) {
          .container {
            padding: 15px;
          }
          
          .main-content {
            grid-template-columns: 1fr !important;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default UuidGeneratorPage;

